
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { namespace } from 'vuex-class';
import { vuex } from "@/store";
import _ from 'lodash';
import rest from '@/rest';
import { signalrEventBus } from '@/main';
import { List } from 'linq-collections';
// components
import MediaPool from '@/components/ContentCreator/MediaPool.vue';
import L3FrameWithTitle from '@/components/Shared/L3FrameWithTitle.vue';
import SimpleMessageBox from '@/components/Shared/SimpleMessageBox.vue';
// interfaces
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import { CourseElementVm } from '@/interfaces/CourseElementVm';
import { ParamDictionary } from '@/interfaces/ParamDictionary';
import { CustomizationVm } from '@/interfaces/CustomizationVm';
import { AiVideoScenesWizardVm, AiVideoSceneVm } from '@/interfaces/AiVideoScenesWizardVm';
import { AiVideoCreatorVm } from '@/interfaces/AiVideoCreatorVm';


const globals = namespace("globals");
const ux = namespace("ux");
const customization = namespace("customization");

@Component({
  components: {
    L3FrameWithTitle,
    MediaPool,
    SimpleMessageBox
  }
})
export default class AiVideoScenesWizardDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @PropSync("videoCreatorData") syncedVideoCreatorData!: AiVideoCreatorVm;
  @Prop() videoElement!: CourseElementVm;
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle: any;
  @globals.Getter selectedExercise: any;
  @globals.Action refreshSelectedElement!: () => Promise<void>;
  @ux.Getter gradientBackground: any;
  @ux.Getter gradientBackground2: any;
  @ux.Getter gradientBackground3: any;
  @ux.Getter gradientText: any;
  @ux.State darkTheme: any;

  isBusyOnDescriptionSuggestion = false;
  isBusyOnTargetGroupAndToneSuggestion = false;
  isBusySavingData = false;
  isBusyCreatingScenes = false;
  isValid = false;
  showConfirmationDialog = false;
  showImportDialog = false;
  importText = '';
  clipboardCopied = false;
  fileWithSummarySelected = false;
  formData: AiVideoScenesWizardVm | null = null;
  originalFormData: AiVideoScenesWizardVm | null = null;
  fileList: FileDetailsVm[] | null = null;
  // chatMessages: ChatMessageVm[] = [];
  id = "";
  videoCreationProgress = 0;

  $refs!: {
    mediaPool: MediaPool;
  };

  created() {
    // SignalR event listener
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  // SignalR events
  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    if (this.id !== response.id) return;
    console.log("AiVideoCreatorWizardDlg > Background task event:", response);

    if (response.status == "INF") {
      this.videoCreationProgress = response.progress;
      return;
    }

    await this.refreshSelectedElement();
    this.videoCreationProgress = 0;
    this.isBusyCreatingScenes = false;

    if (response.status == "SUC") {
      this.syncedShowDialog = false;
    }
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      this.isBusySavingData = false;
      this.isBusyCreatingScenes = false;
      this.videoCreationProgress = 0;

      await this.loadAiVideoScenesData();
      // console.log("AiVideoCreatorWizardDlg: Language ", this.formData?.language);
      this.onFileSelectionChanged();
      if (this.formData && !this.formData.language) {
        // Default language code (Deutsch)
        console.log("AiVideoCreatorWizardDlg: Setting default language code to de-DE");
        this.formData!.language = "de-DE";
      }

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        disableBodyScroll(modal);
      });
    } else {
      // Dialog closing
      clearAllBodyScrollLocks();
    }
  }

  get languageOptions() {
    return this.$globalHelper.euLanguageOptions;
  }

  selectVideoSettings(videoArchetype: string) {
    if (!this.formData || !this.formData.videoArchetype) return;

    const mapping: Record<
      string,
      {
        didacticApproach: string
        presentationFormat: string
        visualStyle: string
      }
    > = {
      introductory: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'mixed_media',
        visualStyle: 'clean_modern'
      },

      moderation: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'talking_head',
        visualStyle: 'friendly_colorful'
      },

      classic_explainer: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'whiteboard',
        visualStyle: 'clean_modern'
      },

      animated_explainer: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'animated_explainer',
        visualStyle: 'friendly_colorful'
      },

      software_tutorial: {
        didacticApproach: 'tutorial_practical',
        presentationFormat: 'screencast',
        visualStyle: 'minimalist'
      },

      coding_walkthrough: {
        didacticApproach: 'tutorial_practical',
        presentationFormat: 'screencast',
        visualStyle: 'tech_futuristic'
      },

      exam_crash_course: {
        didacticApproach: 'exam_focused',
        presentationFormat: 'slides_graphics',
        visualStyle: 'academic'
      },

      science_story_video: {
        didacticApproach: 'story_based',
        presentationFormat: 'animated_explainer',
        visualStyle: 'youtube_education'
      },

      documentary_learning: {
        didacticApproach: 'story_based',
        presentationFormat: 'documentary',
        visualStyle: 'cinematic'
      },

      business_case_study: {
        didacticApproach: 'case_study',
        presentationFormat: 'slides_graphics',
        visualStyle: 'corporate'
      },

      interactive_coach: {
        didacticApproach: 'coaching_dialogue',
        presentationFormat: 'talking_head',
        visualStyle: 'friendly_colorful'
      },

      educational_podcast: {
        didacticApproach: 'exploratory',
        presentationFormat: 'podcast_visualized',
        visualStyle: 'premium_dark'
      },

      youtube_education_style: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'mixed_media',
        visualStyle: 'youtube_education'
      },

      tiktok_learning_clip: {
        didacticApproach: 'challenge_based',
        presentationFormat: 'social_short',
        visualStyle: 'neon_dynamic'
      },

      university_lecture: {
        didacticApproach: 'direct_explainer',
        presentationFormat: 'slides_graphics',
        visualStyle: 'academic'
      },

      startup_product_training: {
        didacticApproach: 'tutorial_practical',
        presentationFormat: 'mixed_media',
        visualStyle: 'clean_modern'
      },

      historical_deep_dive: {
        didacticApproach: 'story_based',
        presentationFormat: 'documentary',
        visualStyle: 'cinematic'
      },

      medical_training: {
        didacticApproach: 'case_study',
        presentationFormat: 'mixed_media',
        visualStyle: 'scientific_visual'
      },

      engineering_explained: {
        didacticApproach: 'problem_oriented',
        presentationFormat: 'whiteboard',
        visualStyle: 'tech_futuristic'
      },

      motivational_learning: {
        didacticApproach: 'story_based',
        presentationFormat: 'talking_head',
        visualStyle: 'cinematic'
      },

      kids_education: {
        didacticApproach: 'exploratory',
        presentationFormat: 'animated_explainer',
        visualStyle: 'playful'
      },

      data_analytics_breakdown: {
        didacticApproach: 'comparative',
        presentationFormat: 'slides_graphics',
        visualStyle: 'scientific_visual'
      }
    }

    const settings = mapping[this.formData.videoArchetype] || {
      didacticApproach: 'direct_explainer',
      presentationFormat: 'mixed_media',
      visualStyle: 'clean_modern'
    }

    this.formData.didacticApproach = settings.didacticApproach
    this.formData.presentationFormat = settings.presentationFormat
    this.formData.visualStyle = settings.visualStyle
  }

  async loadAiVideoScenesData() {
    // this.isBusySavingData = true;
    let params = <ParamDictionary>{dictionary: {}};
    params.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    params.dictionary!["ExerciseId"] = this.selectedExercise.id.toString();
    // params.dictionary!["FileName"] = '[Video] ' + this.videoElement.guid!;
    params.dictionary!["ElementGuid"] = this.videoElement.guid!;

    this.formData = null;
    await rest.url("contentManager/loadAiVideoScenesAssistantData")
      .post(params)
      .then((response) => {
        // console.log("AiVideoScenesWizardDlg - loadAiVideoScenesData language:", response?.language);
        this.formData = response;
        this.formData!.bundleId = this.selectedBundle.id;

        if (this.formData && !this.formData.scenes) {
          this.formData.scenes = [];
        }

        // if (this.formData!.scenes!.length == 0)
        //   this.formData!.scenes!.push(<AiVideoSceneVm>{ id: 1, name: "Szene 1", startFrameFiles: [] });

        // Keep a deep copy of the loaded data to detect unsaved changes
        this.originalFormData = _.cloneDeep(this.formData);
        // console.log("AiVideoScenesWizardDlg - loadAiVideoScenesData:", this.formData);
        // console.log("AiVideoScenesWizardDlg - originalFormData:", this.originalFormData);
      })
      .finally(() => {
        // this.isBusySavingData = false;
      });
  }

  async saveAiVideoScenesWizardData() {
    this.isBusySavingData = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/saveAiVideoScenesAssistantData")
      .post(this.formData)
      .then((response) => {
        // After a successful save, update the original snapshot so further closes don't prompt
        this.originalFormData = _.cloneDeep(this.formData);
      })
      .finally(() => {
        this.isBusySavingData = false;
      });
  }

  async onVideoDescriptionSuggestion() {
    this.isBusyOnDescriptionSuggestion = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/suggestAiVideoContentDescription")
      .post(this.formData)
      .then((response) => {
        if (! this.formData) return;
        this.formData.text = response.text;
        this.formData.learningObjective = response.learningObjective;
      })
      .finally(() => {
        this.isBusyOnDescriptionSuggestion = false;
      });
  }

  async onTargetGroupAndToneSuggestion() {
    this.isBusyOnTargetGroupAndToneSuggestion = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/suggestAiVideoTargetGroupAndTone")
      .post(this.formData)
      .then((response) => {
        if (! this.formData) return;
        this.formData.targetGroup = response.targetGroup;
        this.formData.tone = response.tone;
        this.formData.priorKnowledge = response.priorKnowledge;
      })
      .finally(() => {
        this.isBusyOnTargetGroupAndToneSuggestion = false;
      });
  }

  async onCreateScenes() {
    this.isBusyCreatingScenes = true;
    this.fileList = this.$refs.mediaPool.getSelectedFiles();
    this.formData!.mediaAiSummaryFiles = this.fileList;

    await rest.url("contentManager/createVideoScenes")
      .post(this.formData)
      .then((response) => {
        if (!this.formData) return;
        this.formData.scenes = response.scenes;
        this.formData.actorFiles = response.actorFiles;
        this.formData.sceneryFiles = response.sceneryFiles;
        this.formData.objectFiles = response.objectFiles;
        // this.syncedVideoElement.createdWithAi = true;
      })
      .finally(() => {
        this.isBusyCreatingScenes = false;
      });
  }

  async onAssignScenes() {
    if (!this.syncedVideoCreatorData || !this.formData) return;
    await this.saveAiVideoScenesWizardData();
    this.syncedVideoCreatorData.scenes = this.formData.scenes;
    console.log("Assigning actores to video creator data:", this.formData.actorFiles);
    this.syncedVideoCreatorData.assetFiles = this.formData.actorFiles!;
    this.syncedVideoCreatorData.assetFiles.push(...this.formData.sceneryFiles!);
    this.syncedVideoCreatorData.assetFiles.push(...this.formData.objectFiles!);
    this.$emit('updated:videoScenesWizard');
    this.syncedShowDialog = false;
  }

  async onClose() {
    // If we don't have loaded data, just close
    if (!this.formData || !this.originalFormData) {
      this.$emit('closed:AiVideoScenesWizard');
      this.syncedShowDialog = false;
      return;
    }

    // If nothing changed, close immediately
    if (_.isEqual(this.formData, this.originalFormData)) {
      this.$emit('closed:AiVideoScenesWizard');
      this.syncedShowDialog = false;
      return;
    }

    // Otherwise ask for confirmation
    this.showConfirmationDialog = true;
  }

  async onCloseEditor(save: boolean) {
    this.showConfirmationDialog = false;
    if (save) {
      await this.saveAiVideoScenesWizardData();
    }
    this.$emit('closed:AiVideoScenesWizard');
    this.syncedShowDialog = false;
  }

  onFileSelectionChanged() {
    if (!this.$refs.mediaPool) return;
    let files = this.$refs.mediaPool.getSelectedFiles().filter(f => f.hasAiSummary);
    this.fileWithSummarySelected = files.length > 0;
  }

  get enableTargetGroupAndToneSuggestion() {
    if (!this.formData) return false;
    if (this.formData.text?.length ?? 0 > 0) return true;
    if (this.fileWithSummarySelected) return true;

    return false;
  }

  getChatBubbleColor(speaker: string) {
    if (speaker == 'Host') {
      return '#e0e0e0'; // grey lighten-2
    }
    if (this.darkTheme) {
      return this.$store.state.ux.colorAccent + '60';
    } else {
      return this.$store.state.ux.colorAccent + 'e0';
    }
  }

  onOpenEditDialog() {
    // this.importText = this.formData?.dialog?.map(m => `[${m.speaker}]\n${m.text}`).join('\n') ?? '';
    this.showImportDialog = true;
  }

  async copyScenesToClipboard() {
    // if (!this.formData?.dialog) return;
    // this.clipboardCopied = true;
    // const text = this.formData.dialog
    //   .map(m => `[${m.speaker}]\n${m.text}`)
    //   .join('\n');
    // navigator.clipboard.writeText(text);
    // await this.$globalHelper.delay(2000);
    // this.clipboardCopied = false;
  }

  onImportScenes() {
    if (!this.formData || !this.importText.trim()) return;
    // const lines = this.importText.split('\n');
    // const parsed: PodcastTextVm[] = [];
    // let current: PodcastTextVm | null = null;

    // for (const line of lines) {
    //   const match = line.match(/^\[(Host|Guest)\]\s*(.*)$/);
    //   if (match) {
    //     if (current) {
    //       if (current.text) {
    //         current.text = current.text.trim();
    //       }
    //       parsed.push(current);
    //     }
    //     current = <PodcastTextVm>{ id: 0, speaker: match[1], text: match[2] };
    //   } else if (current && line.trim().length > 0) {
    //     // continuation line (skip blank lines)
    //     current.text += ((current.text ?? '').length > 0 ? '\n' : '') + line;
    //   }
    // }
    // if (current) {
    //   const last = current;
    //   if (last.text) {
    //     last.text = last.text.trim();
    //   }
    //   parsed.push(last);
    // }

    // if (parsed.length === 0) return;
    // this.formData.dialog = parsed;
    // this.updateDialogIds();
    this.showImportDialog = false;
    this.importText = '';
  }

  get dialogBackground() {
    if (this.darkTheme)
      return "#616161";
    else
      return "#d0d0d0";
  }
}
